import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { clearCollectionTracks, getCollectionTracks } from '@/store/tracks/actions';

import { selectTracks } from '@/store/tracks/selectors';

import { Form, Layout, Pagination, Spin, Typography } from 'antd';
import CollectionTrackCard from '@/components/CollectionTrackCard';
import CustomEmpty from '@/components/CustomEmpty';
import { Content } from '@/components/Layout';

import CollectionTracksFilter from './parts/CollectionTracksFilter';

import Sort from '@shared/components/NewSort';
import { SortTypeHelp } from '@shared/components/NewSort/constants';

import { DEFAULT_COLLECTION_TRACKS_SORT } from '@/store/tracks/reducer';

import css from './CollectionTracks.module.scss';

const { Title } = Typography;

const CollectionTracks = () => {
  const { t } = useTranslation('collectionTracks');
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [searchParams, setSearchParams] = useSearchParams();

  const { collectionTracks, collectionTracksPagination, collectionTracksSort, isLoading } = useSelector(selectTracks);

  const sortTypes = [
    {
      name: t('date'),
      value: ['createdDate,asc', 'createdDate,desc'],
      type: SortTypeHelp.DATE,
    },
    {
      name: t('name'),
      value: ['name,asc', 'name,desc'],
      type: SortTypeHelp.ALPHABET,
    },
  ];

  // searchParams в объекте
  const paramsObject = Object.fromEntries(searchParams.entries());

  useEffect(() => {
    const sort = paramsObject?.sort || collectionTracksSort;
    const page = paramsObject?.page || 0;

    dispatch(getCollectionTracks(form.getFieldsValue(), page, sort));

    return () => {
      form.resetFields();
      dispatch(clearCollectionTracks());
    };
  }, []);

  const onAddToPlanSuccess = () =>
    dispatch(getCollectionTracks(form.getFieldsValue(), collectionTracksPagination.currentPage, collectionTracksSort));

  const collectionTracksList = useMemo(() => {
    if (collectionTracks.length === 0 && isLoading) {
      return (
        <div className={css['CollectionTracks-body-list']}>
          <CollectionTrackCard.Skeleton />
          <CollectionTrackCard.Skeleton />
          <CollectionTrackCard.Skeleton />
          <CollectionTrackCard.Skeleton />
        </div>
      );
    }

    if (collectionTracks.length === 0) {
      const hasFilterValues = Object.values(form.getFieldsValue()).some(value => !!value);

      return (
        <CustomEmpty
          className={css['CollectionTracks-empty']}
          title={t('emptyTitle')}
          description={hasFilterValues ? t('emptyDescriptionWithFilters') : t('emptyDescription')}
        />
      );
    }

    return (
      <div className={css['CollectionTracks-body-list']}>
        {collectionTracks.map(track => (
          <CollectionTrackCard key={track?.id} track={track} onAddToPlanSuccess={onAddToPlanSuccess} />
        ))}
      </div>
    );
  }, [collectionTracks, isLoading]);

  const handleChangePagination = page => {
    dispatch(getCollectionTracks(form.getFieldsValue(), page - 1, collectionTracksSort));
    setSearchParams({ ...paramsObject, page: page - 1 });
  };

  const handleChangeSort = sort => {
    dispatch(getCollectionTracks(form.getFieldsValue(), collectionTracksPagination.currentPage, sort));
    setSearchParams({ ...paramsObject, sort });
  };

  // Функции для фильтра
  const onSubmit = () => {
    dispatch(getCollectionTracks(form.getFieldsValue(), 0, collectionTracksSort));
  };

  const onReset = () => {
    form.resetFields();
    dispatch(getCollectionTracks(form.getFieldsValue(), 0, DEFAULT_COLLECTION_TRACKS_SORT));
    setSearchParams({ sort: DEFAULT_COLLECTION_TRACKS_SORT });
  };

  return (
    <Layout>
      <Content className={css['CollectionTracks']}>
        {/* Header */}
        <Title data-qa='collectionTracksTitle' level={1} className={css['CollectionTracks-title']}>
          {t('title')}
        </Title>

        {/* Filter */}
        <CollectionTracksFilter form={form} onSubmit={onSubmit} onReset={onReset} />

        {/* Body */}
        <div className={css['CollectionTracks-body']}>
          <Sort
            activeSort={collectionTracksSort}
            sortTypes={sortTypes}
            handleChangeSort={handleChangeSort}
            className={css['CollectionTracks-body-sort']}
            isLoading={isLoading}
          />

          <Spin spinning={isLoading && collectionTracks.length !== 0}>{collectionTracksList}</Spin>

          <div className={css['CollectionTracks-body-pagination']}>
            <Pagination
              current={collectionTracksPagination.currentPage + 1}
              pageSize={collectionTracksPagination.pageSize}
              total={collectionTracksPagination.totalElements}
              onChange={handleChangePagination}
              showSizeChanger={false}
              hideOnSinglePage
            />
          </div>
        </div>
      </Content>
    </Layout>
  );
};

export default CollectionTracks;
